/**
 * PSP/CRE Tool v2.0 - Infrastructure Status Constants
 * 
 * Purpose: Define infrastructure conditions affecting medical operations
 * including power, communications, water, and local facilities.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 21, 2024
 * Version: 2.0 - Enhanced with icons.js integration
 * 
 * References:
 * - NATO AJP-4.10 Allied Joint Doctrine for Medical Support
 * - FM 4-02 Army Health System
 * - Ukraine 2022-2024 infrastructure lessons learned
 * 
 * Usage:
 * import { 
 *   POWER_STATUS,
 *   COMMS_STATUS,
 *   WATER_STATUS,
 *   getInfrastructureModifiers
 * } from '../constants/infrastructureStatus';
 */

import { ICONS, STATUS_ICONS, MEDICAL_ICONS } from '../utils/icons';

// ============================================================================
// POWER STATUS
// ============================================================================

export const POWER_STATUS = {
  GRID_STABLE: {
    value: 'GRID_STABLE',
    label: 'Grid Power Stable',
    shortLabel: 'STABLE',
    description: 'Reliable civilian grid power available',
    icon: ICONS.STATUS.POWER_ON,
    color: 'success',
    
    generator_dependency: 0,
    fuel_consumption_modifier: 0.1,
    capacity_modifier: 1.0,
    equipment_availability: 1.0,
    
    surgical_capacity: 1.0,
    imaging_available: true,
    blood_storage_reliable: true,
    hvac_operational: true,
    
    notes: 'Optimal conditions. Generators on standby only.'
  },
  
  GRID_INTERMITTENT: {
    value: 'GRID_INTERMITTENT',
    label: 'Grid Intermittent',
    shortLabel: 'INTERMIT',
    description: 'Scheduled or unscheduled power outages',
    icon: STATUS_ICONS.YELLOW,
    color: 'warning',
    
    generator_dependency: 0.5,
    fuel_consumption_modifier: 1.5,
    capacity_modifier: 0.9,
    equipment_availability: 0.95,
    
    surgical_capacity: 0.9,
    imaging_available: true,
    blood_storage_reliable: true,
    hvac_operational: true,
    
    outage_frequency: '2-4 per day',
    outage_duration_hours: '1-4',
    
    ukraine_data: {
      applicable: true,
      notes: 'Common after infrastructure strikes, especially winter 2022-23'
    },
    
    notes: 'Plan for generator switchover. Maintain fuel reserves.'
  },
  
  GENERATOR_PRIMARY: {
    value: 'GENERATOR_PRIMARY',
    label: 'Generator Primary',
    shortLabel: 'GENSET',
    description: 'No grid - full generator dependency',
    icon: ICONS.STATUS.POWER_LOW,
    color: 'warning',
    
    generator_dependency: 1.0,
    fuel_consumption_modifier: 3.0,
    capacity_modifier: 0.85,
    equipment_availability: 0.9,
    
    surgical_capacity: 0.85,
    imaging_available: true,
    blood_storage_reliable: true,
    hvac_operational: true,
    
    fuel_requirement_liters_per_day: {
      role_1: 100,
      role_2: 500,
      role_3: 1500
    },
    
    ukraine_data: {
      applicable: true,
      notes: 'Standard for forward MTFs; fuel logistics critical'
    },
    
    notes: 'High fuel consumption. Plan resupply. Prioritize critical loads.'
  },
  
  GENERATOR_LIMITED: {
    value: 'GENERATOR_LIMITED',
    label: 'Limited Generator',
    shortLabel: 'LIMITED',
    description: 'Insufficient generator capacity',
    icon: STATUS_ICONS.YELLOW_WARNING,
    color: 'error',
    
    generator_dependency: 1.0,
    fuel_consumption_modifier: 2.0,
    capacity_modifier: 0.6,
    equipment_availability: 0.7,
    
    surgical_capacity: 0.6,
    imaging_available: false,
    blood_storage_reliable: false,
    hvac_operational: false,
    
    notes: 'Critical only. No elective procedures. Blood products at risk.'
  },
  
  NO_POWER: {
    value: 'NO_POWER',
    label: 'No Power',
    shortLabel: 'NONE',
    description: 'No electrical power available',
    icon: STATUS_ICONS.RED,
    color: 'error',
    
    generator_dependency: 0,
    fuel_consumption_modifier: 0,
    capacity_modifier: 0.3,
    equipment_availability: 0.2,
    
    surgical_capacity: 0.2,
    imaging_available: false,
    blood_storage_reliable: false,
    hvac_operational: false,
    
    notes: 'Emergency procedures only. Evacuate patients if possible.'
  }
};

// ============================================================================
// COMMUNICATIONS STATUS
// ============================================================================

export const COMMS_STATUS = {
  FULL_SPECTRUM: {
    value: 'FULL_SPECTRUM',
    label: 'Full Spectrum',
    shortLabel: 'FULL',
    description: 'All military and civilian comms operational',
    icon: ICONS.GENERAL.RADIO,
    color: 'success',
    
    capabilities: {
      military_radio: true,
      satellite: true,
      cellular: true,
      landline: true,
      internet: true
    },
    
    medevac_coordination: 1.0,
    situational_awareness: 1.0,
    evacuation_delay_modifier: 1.0,
    
    notes: 'Optimal communications. Real-time coordination possible.'
  },
  
  MILITARY_ONLY: {
    value: 'MILITARY_ONLY',
    label: 'Military Only',
    shortLabel: 'MIL',
    description: 'Civilian infrastructure down, military comms working',
    icon: ICONS.GENERAL.RADIO,
    color: 'info',
    
    capabilities: {
      military_radio: true,
      satellite: true,
      cellular: false,
      landline: false,
      internet: false
    },
    
    medevac_coordination: 0.9,
    situational_awareness: 0.85,
    evacuation_delay_modifier: 1.1,
    
    ukraine_data: {
      applicable: true,
      notes: 'Common in contested areas after infrastructure strikes'
    },
    
    notes: 'Rely on military nets. No civilian hospital coordination.'
  },
  
  DEGRADED: {
    value: 'DEGRADED',
    label: 'Degraded',
    shortLabel: 'DEGRADED',
    description: 'Intermittent communications, jamming present',
    icon: STATUS_ICONS.YELLOW,
    color: 'warning',
    
    capabilities: {
      military_radio: true,
      satellite: true,
      cellular: false,
      landline: false,
      internet: false
    },
    
    medevac_coordination: 0.7,
    situational_awareness: 0.6,
    evacuation_delay_modifier: 1.3,
    
    jamming_present: true,
    backup_procedures_required: true,
    
    ukraine_data: {
      applicable: true,
      notes: 'Russian EW caused significant comm degradation; Starlink critical'
    },
    
    notes: 'Use backup frequencies. Plan for comm windows. Runner system.'
  },
  
  MINIMAL: {
    value: 'MINIMAL',
    label: 'Minimal',
    shortLabel: 'MIN',
    description: 'Very limited communications capability',
    icon: STATUS_ICONS.YELLOW_WARNING,
    color: 'error',
    
    capabilities: {
      military_radio: false,
      satellite: true,
      cellular: false,
      landline: false,
      internet: false
    },
    
    medevac_coordination: 0.4,
    situational_awareness: 0.4,
    evacuation_delay_modifier: 1.5,
    
    notes: 'Satellite only. Pre-planned evacuation windows. Courier system.'
  },
  
  DENIED: {
    value: 'DENIED',
    label: 'Comms Denied',
    shortLabel: 'DENIED',
    description: 'No reliable communications',
    icon: STATUS_ICONS.RED,
    color: 'error',
    
    capabilities: {
      military_radio: false,
      satellite: false,
      cellular: false,
      landline: false,
      internet: false
    },
    
    medevac_coordination: 0.1,
    situational_awareness: 0.2,
    evacuation_delay_modifier: 2.0,
    
    notes: 'Autonomous operations. Pre-planned schedules. Physical runners.'
  }
};

// ============================================================================
// WATER STATUS
// ============================================================================

export const WATER_STATUS = {
  MUNICIPAL_SAFE: {
    value: 'MUNICIPAL_SAFE',
    label: 'Municipal Safe',
    shortLabel: 'SAFE',
    description: 'Clean municipal water available',
    icon: STATUS_ICONS.GREEN,
    color: 'success',
    
    potable: true,
    medical_grade: true,
    quantity_sufficient: true,
    purification_required: false,
    
    water_consumption_modifier: 1.0,
    sterilization_capacity: 1.0,
    
    notes: 'Optimal conditions. Standard procedures.'
  },
  
  MUNICIPAL_UNRELIABLE: {
    value: 'MUNICIPAL_UNRELIABLE',
    label: 'Municipal Unreliable',
    shortLabel: 'UNRELIABLE',
    description: 'Intermittent or questionable municipal supply',
    icon: STATUS_ICONS.YELLOW,
    color: 'warning',
    
    potable: false,
    medical_grade: false,
    quantity_sufficient: true,
    purification_required: true,
    
    water_consumption_modifier: 1.2,
    sterilization_capacity: 0.9,
    dnbi_modifier: 1.1,
    
    ukraine_data: {
      applicable: true,
      notes: 'Common after infrastructure strikes; water treatment disrupted'
    },
    
    notes: 'Test before use. Purification recommended.'
  },
  
  WELL_WATER: {
    value: 'WELL_WATER',
    label: 'Well/Ground Water',
    shortLabel: 'WELL',
    description: 'Local well or ground water source',
    icon: STATUS_ICONS.BLUE,
    color: 'warning',
    
    potable: false,
    medical_grade: false,
    quantity_sufficient: true,
    purification_required: true,
    
    water_consumption_modifier: 1.3,
    sterilization_capacity: 0.85,
    dnbi_modifier: 1.2,
    
    notes: 'Deploy ROWPU. Test water quality regularly.'
  },
  
  BOTTLED_ONLY: {
    value: 'BOTTLED_ONLY',
    label: 'Bottled/Trucked Only',
    shortLabel: 'BOTTLED',
    description: 'No local water - supply chain dependent',
    icon: STATUS_ICONS.YELLOW_WARNING,
    color: 'warning',
    
    potable: true,
    medical_grade: true,
    quantity_sufficient: false,
    purification_required: false,
    
    water_consumption_modifier: 2.0,
    sterilization_capacity: 0.7,
    
    logistics_dependent: true,
    liters_per_patient_per_day: 15,
    
    notes: 'Plan resupply carefully. Conserve water. Limit procedures.'
  },
  
  NO_WATER: {
    value: 'NO_WATER',
    label: 'No Water',
    shortLabel: 'NONE',
    description: 'No water available',
    icon: STATUS_ICONS.RED,
    color: 'error',
    
    potable: false,
    medical_grade: false,
    quantity_sufficient: false,
    purification_required: false,
    
    water_consumption_modifier: 0,
    sterilization_capacity: 0.3,
    surgical_capacity_modifier: 0.3,
    
    notes: 'Emergency only. Evacuate if possible. Request immediate resupply.'
  }
};

// ============================================================================
// LOCAL MEDICAL INFRASTRUCTURE
// ============================================================================

export const LOCAL_MEDICAL_STATUS = {
  FUNCTIONAL: {
    value: 'FUNCTIONAL',
    label: 'Hospitals Functional',
    shortLabel: 'FUNC',
    description: 'Local civilian hospitals operational',
    icon: MEDICAL_ICONS.HOSPITAL,
    color: 'success',
    
    civilian_hospitals_available: true,
    hns_medical_support: true,
    civilian_evacuation_possible: true,
    blood_bank_access: true,
    
    capacity_supplement: 0.3,
    
    notes: 'Coordinate HNS agreements. Civilian surge capability available.'
  },
  
  OVERWHELMED: {
    value: 'OVERWHELMED',
    label: 'Hospitals Overwhelmed',
    shortLabel: 'OVERWHLM',
    description: 'Civilian hospitals at/over capacity',
    icon: STATUS_ICONS.YELLOW_WARNING,
    color: 'warning',
    
    civilian_hospitals_available: true,
    hns_medical_support: false,
    civilian_evacuation_possible: false,
    blood_bank_access: true,
    
    capacity_supplement: 0,
    civilian_casualty_load: 'HIGH',
    
    ukraine_data: {
      applicable: true,
      notes: 'Common in cities under attack; military often supported civilian hospitals'
    },
    
    notes: 'No civilian support available. May need to support civilians.'
  },
  
  DAMAGED: {
    value: 'DAMAGED',
    label: 'Hospitals Damaged',
    shortLabel: 'DAMAGED',
    description: 'Local hospitals damaged, limited function',
    icon: STATUS_ICONS.YELLOW,
    color: 'error',
    
    civilian_hospitals_available: false,
    hns_medical_support: false,
    civilian_evacuation_possible: false,
    blood_bank_access: false,
    
    capacity_supplement: 0,
    civilian_casualty_responsibility: true,
    
    ukraine_data: {
      applicable: true,
      notes: 'Multiple hospitals struck; Mariupol maternity hospital attack March 2022'
    },
    
    notes: 'Military may need to treat civilians. Plan for surge.'
  },
  
  DESTROYED: {
    value: 'DESTROYED',
    label: 'No Local Medical',
    shortLabel: 'NONE',
    description: 'No civilian medical infrastructure',
    icon: STATUS_ICONS.RED,
    color: 'error',
    
    civilian_hospitals_available: false,
    hns_medical_support: false,
    civilian_evacuation_possible: false,
    blood_bank_access: false,
    
    capacity_supplement: 0,
    civilian_casualty_responsibility: true,
    civilian_population_at_risk: true,
    
    notes: 'Full military responsibility. Civilian MASCAL likely. CMO coordination.'
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get combined infrastructure modifiers
 */
export function getInfrastructureModifiers(infraConfig) {
  const {
    power = 'GRID_STABLE',
    communications = 'FULL_SPECTRUM',
    water = 'MUNICIPAL_SAFE',
    local_medical = 'FUNCTIONAL'
  } = infraConfig;
  
  const powerData = POWER_STATUS[power] || POWER_STATUS.GRID_STABLE;
  const commsData = COMMS_STATUS[communications] || COMMS_STATUS.FULL_SPECTRUM;
  const waterData = WATER_STATUS[water] || WATER_STATUS.MUNICIPAL_SAFE;
  const medicalData = LOCAL_MEDICAL_STATUS[local_medical] || LOCAL_MEDICAL_STATUS.FUNCTIONAL;
  
  const capacityModifier = Math.min(
    powerData.capacity_modifier,
    waterData.sterilization_capacity || 1.0
  );
  
  const evacDelayModifier = commsData.evacuation_delay_modifier || 1.0;
  const dnbiModifier = waterData.dnbi_modifier || 1.0;
  
  const surgicalCapacity = Math.min(
    powerData.surgical_capacity || 1.0,
    waterData.surgical_capacity_modifier || 1.0
  );
  
  const warnings = [];
  
  if (powerData.value === 'NO_POWER') {
    warnings.push({
      severity: 'error',
      icon: STATUS_ICONS.RED,
      category: 'POWER',
      message: 'No electrical power - emergency operations only'
    });
  } else if (powerData.value === 'GENERATOR_LIMITED') {
    warnings.push({
      severity: 'warning',
      icon: STATUS_ICONS.YELLOW_WARNING,
      category: 'POWER',
      message: 'Limited generator capacity - reduced operations'
    });
  }
  
  if (!powerData.blood_storage_reliable) {
    warnings.push({
      severity: 'error',
      icon: STATUS_ICONS.RED,
      category: 'BLOOD',
      message: 'Blood storage unreliable - products at risk'
    });
  }
  
  if (commsData.value === 'DENIED' || commsData.value === 'MINIMAL') {
    warnings.push({
      severity: 'error',
      icon: STATUS_ICONS.RED,
      category: 'COMMS',
      message: 'Communications severely degraded'
    });
  }
  
  if (waterData.value === 'NO_WATER') {
    warnings.push({
      severity: 'error',
      icon: STATUS_ICONS.RED,
      category: 'WATER',
      message: 'No water available'
    });
  }
  
  if (medicalData.civilian_casualty_responsibility) {
    warnings.push({
      severity: 'warning',
      icon: STATUS_ICONS.YELLOW_WARNING,
      category: 'CIVILIAN',
      message: 'Military may need to treat civilian casualties'
    });
  }
  
  let overallStatus = 'GREEN';
  let overallIcon = STATUS_ICONS.GREEN;
  
  if (warnings.some(w => w.severity === 'error')) {
    overallStatus = 'RED';
    overallIcon = STATUS_ICONS.RED;
  } else if (warnings.some(w => w.severity === 'warning')) {
    overallStatus = 'YELLOW';
    overallIcon = STATUS_ICONS.YELLOW;
  }
  
  return {
    capacity_modifier: Math.round(capacityModifier * 100) / 100,
    surgical_capacity: Math.round(surgicalCapacity * 100) / 100,
    evac_delay_modifier: Math.round(evacDelayModifier * 100) / 100,
    dnbi_modifier: Math.round(dnbiModifier * 100) / 100,
    imaging_available: powerData.imaging_available !== false,
    blood_storage_reliable: powerData.blood_storage_reliable !== false,
    medevac_coordination: commsData.medevac_coordination || 1.0,
    hns_available: medicalData.hns_medical_support || false,
    generator_dependency: powerData.generator_dependency || 0,
    fuel_consumption_modifier: powerData.fuel_consumption_modifier || 1.0,
    water_purification_required: waterData.purification_required || false,
    overall_status: overallStatus,
    overall_icon: overallIcon,
    warnings
  };
}

/**
 * Validate infrastructure configuration
 */
export function validateInfrastructureConfig(infraConfig) {
  const errors = [];
  const warnings = [];
  
  if (!POWER_STATUS[infraConfig.power]) errors.push('Invalid power status');
  if (!COMMS_STATUS[infraConfig.communications]) errors.push('Invalid communications status');
  if (!WATER_STATUS[infraConfig.water]) errors.push('Invalid water status');
  if (!LOCAL_MEDICAL_STATUS[infraConfig.local_medical]) errors.push('Invalid local medical status');
  
  if (infraConfig.power === 'NO_POWER' && infraConfig.water === 'NO_WATER') {
    errors.push('No power and no water - facility cannot operate');
  }
  
  return { 
    errors, 
    warnings, 
    isValid: errors.length === 0,
    icon: errors.length > 0 ? STATUS_ICONS.RED : STATUS_ICONS.GREEN
  };
}

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_INFRASTRUCTURE_CONFIG = {
  power: 'GRID_STABLE',
  communications: 'FULL_SPECTRUM',
  water: 'MUNICIPAL_SAFE',
  local_medical: 'FUNCTIONAL'
};

export const AUSTERE_INFRASTRUCTURE_CONFIG = {
  power: 'GENERATOR_PRIMARY',
  communications: 'MILITARY_ONLY',
  water: 'WELL_WATER',
  local_medical: 'DAMAGED'
};

export const CONTESTED_INFRASTRUCTURE_CONFIG = {
  power: 'GENERATOR_LIMITED',
  communications: 'DEGRADED',
  water: 'BOTTLED_ONLY',
  local_medical: 'DESTROYED'
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const infrastructureStatusConstants = {
  POWER_STATUS,
  COMMS_STATUS,
  WATER_STATUS,
  LOCAL_MEDICAL_STATUS,
  getInfrastructureModifiers,
  validateInfrastructureConfig,
  DEFAULT_INFRASTRUCTURE_CONFIG,
  AUSTERE_INFRASTRUCTURE_CONFIG,
  CONTESTED_INFRASTRUCTURE_CONFIG
};

export default infrastructureStatusConstants;
